<template>
  <section class="bg-secondary lg:px-48 md:px-12 px-4 pb-14">
    <div class="max-w-2xl mx-auto">

      <!-- Подяка після відправки -->
      <Transition name="expand">
        <div v-if="success" class="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
          <div class="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" class="text-gray-800">
              <path d="M4 6C7.5 5 10 5.5 12 7C14 5.5 16.5 5 20 6V18C16.5 17 14 17.5 12 19C10 17.5 7.5 17 4 18V6Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M12 7V19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M15.5 11.5L17 13L20 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="font-pt-serif text-2xl font-bold mb-3">{{ t('form.thankYouTitle') }}</h3>
          <p class="font-montserrat text-gray-600 leading-relaxed">{{ t('form.thankYouBody') }}</p>
        </div>
      </Transition>

      <!-- Кнопка відправки -->
      <div v-if="!formOpen && !success" class="flex justify-center">
        <button
          class="flex items-center gap-2 bg-black px-8 py-4 rounded-lg border-2 border-black border-solid text-white font-montserrat hover:opacity-90 transition-opacity"
          @click="formOpen = true"
        >
          {{ t('lesson.submitAnswers') }}
          <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>

      <!-- Expander -->
      <Transition name="expand">
        <div v-if="formOpen" class="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mt-4">
          <h3 class="font-pt-serif text-2xl font-bold mb-6">{{ t('lesson.submitAnswers') }}</h3>

          <Form ref="formRef" @submit="handleSubmit">
            <!-- Ім'я -->
            <div class="mb-5">
              <label class="font-montserrat text-sm opacity-60 block mb-2">{{ t('form.name') }}</label>
              <Field
                v-model="name"
                name="name"
                :rules="nameRules"
                class="w-full border-b-2 border-gray-300 focus:border-black outline-none py-2 font-pt-serif text-lg bg-transparent transition-colors"
              />
              <ErrorMessage name="name" class="text-red-500 font-montserrat text-xs mt-1 block" />
            </div>

            <!-- Телефон -->
            <div class="mb-5">
              <label class="font-montserrat text-sm opacity-60 block mb-2">{{ t('form.phone') }}</label>
              <PhoneField v-model="phone" />
              <p v-if="phoneError" class="text-red-500 font-montserrat text-xs mt-1">{{ phoneError }}</p>
            </div>

            <!-- Месенджер -->
            <div class="mb-8">
              <label class="font-montserrat text-sm opacity-60 block mb-3">{{ t('form.messenger') }}</label>
              <div class="flex gap-3">
                <button
                  type="button"
                  :class="['flex-1 py-3 border-2 font-montserrat font-medium transition-all', messenger === 'tg' ? 'border-black bg-black text-white' : 'border-gray-300 hover:border-gray-500']"
                  @click="messenger = 'tg'"
                >Telegram</button>
                <button
                  type="button"
                  :class="['flex-1 py-3 border-2 font-montserrat font-medium transition-all', messenger === 'wa' ? 'border-black bg-black text-white' : 'border-gray-300 hover:border-gray-500']"
                  @click="messenger = 'wa'"
                >WhatsApp</button>
              </div>
            </div>

            <!-- Згода -->
            <div class="mb-6">
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  v-model="agreed"
                  type="checkbox"
                  class="mt-1 flex-shrink-0 w-4 h-4 accent-black cursor-pointer"
                  required
                >
                <span class="font-montserrat text-sm text-gray-600 leading-relaxed">
                  {{ t('form.privacy') }}
                  <NuxtLink :to="localePath('/privacy-policy')" target="_blank" class="underline hover:opacity-70">{{ t('form.privacyLink') }}</NuxtLink>
                  {{ t('form.and') }}
                  <NuxtLink :to="localePath('/terms')" target="_blank" class="underline hover:opacity-70">{{ t('form.termsLink') }}</NuxtLink>
                </span>
              </label>
            </div>

            <p v-if="error" class="text-red-500 font-montserrat text-sm mb-4">{{ error }}</p>

            <button
              type="submit"
              :disabled="submitting || !agreed"
              class="bg-black text-white px-8 py-4 rounded-lg font-montserrat font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              {{ submitting ? t('form.submitting') : t('form.submit') }}
            </button>
          </Form>
        </div>
      </Transition>

    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Form, Field, ErrorMessage } from 'vee-validate'
import type { Lesson } from '~/types/lesson.types'

interface LessonContentRef {
  getResults: () => Array<Record<string, unknown>>
}

const props = defineProps<{
  lessonToken?: string
  lesson?: Lesson | null
  lessonContentRef?: LessonContentRef | null
}>()

const { t } = useI18n()
const { submitAndRedirect, submitting, error, success } = useSubmission()
const localePath = useLocalePath()

const formOpen = ref(false)
const formRef = ref<InstanceType<typeof Form> | null>(null)
const name = ref('')
const phone = ref('')
const messenger = ref<'tg' | 'wa'>('tg')
const agreed = ref(false)
const phoneError = ref('')

const nameRules = (value: string) => {
  if (!value?.trim()) return "Введіть ваше ім'я"
  if (value.trim().length < 2) return 'Мінімум 2 символи'
  return true
}

function validatePhone(): boolean {
  const digits = phone.value.replace(/\D/g, '')
  if (digits.length < 7) {
    phoneError.value = 'Введіть номер телефону'
    return false
  }
  phoneError.value = ''
  return true
}

async function handleSubmit() {
  const result = await formRef.value?.validate()
  if (!result?.valid) return
  if (!validatePhone()) return

  const studyingResults = props.lessonContentRef?.getResults() ?? []

  await submitAndRedirect({
    name: name.value.trim(),
    phone: phone.value,
    messenger: messenger.value,
    lessonToken: props.lessonToken,
    lesson: props.lesson,
    studyingResults,
  })

  if (!error.value) formOpen.value = false
}
</script>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
